import React, { useEffect, useState } from 'react';
import { Typography, Button, Box, Grid, Avatar, Card } from '@mui/material';
import Education from '../components/Resume/Education.js';
import Experience from '../components/Resume/Experience.js';
import Summary from '../components/Resume/Summary.js';
import Skills from '../components/Resume/Skills.js';

import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

import { useLoader } from '../context/LoaderContext';
import api from '../api.js';
import ColdStartBanner from '../components/ColdStartBanner.js';


const MyResumePage = ({ data }) => {
  const [user, setUser] = useState({});
  const { showLoader, hideLoader } = useLoader();
  const [visible, setVisible] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let timer;

    const fetchUser = async () => {
      try {
        showLoader("Fetching resume data...");
        setVisible(true);
        const response = await api.get('api/resumes/latest');
        setUser(response.data);
        hideLoader();
        setVisible(false);
      } catch (error) {
        hideLoader();
        setVisible(false);
        console.error('Error fetching user data', error);
      }
    };
    fetchUser();
  }, [showLoader, hideLoader]);

  const buttons = [
    { name: "LinkedIn", icon: <LinkedInIcon />, url: `${user.linkedin}` },
    { name: "GitHub", icon: <GitHubIcon />, url: `${user.github}` },
  ];

  const openInNewTab = (url) => {
    const win = window.open(url, '_blank');
    if (win != null) win.focus();
  };

  const filenameFromDisposition = (headerValue) => {
    if (!headerValue) return null;
    const utfMatch = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
    const match = headerValue.match(/filename="?([^"]+)"?/i);
    return match?.[1] || null;
  };

  const downloadResumePdf = async () => {
    try {
      setDownloading(true);
      showLoader('Generating resume PDF...');
      const response = await api.get('api/resumes/latest/pdf', {
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'] || '';
      if (!contentType.includes('pdf')) {
        throw new Error('Resume PDF was not returned by the server');
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const defaultName = [user.firstName, user.lastName, 'Resume']
        .filter(Boolean)
        .join('_')
        .replace(/\s+/g, '_');
      link.href = objectUrl;
      link.download = filenameFromDisposition(response.headers['content-disposition']) || `${defaultName || 'Resume'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading resume PDF', error);
    } finally {
      hideLoader();
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ p: 1, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <ColdStartBanner visible={visible} />
      <Grid container spacing={4} justifyContent="center">
        {/* Profile Card */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: 6, overflow: 'hidden' }}>
            {/* Hero Banner Section - Smaller */}
            <Box
              sx={{
                height: 200,
                backgroundImage: 'url(/Assets/Background%20Image.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative'
              }}
            />
            
            {/* Content Section */}
            <Box sx={{ p: 3, bgcolor: '#ffffff', position: 'relative', mt: -5, zIndex: 1 }}>
              <Grid container spacing={3} alignItems="flex-start">
                {/* Avatar - Left Side */}
                <Grid item xs={12} sm={4} md={3} container justifyContent="center">
                  <Avatar
                    sx={{ width: 140, height: 140, border: '5px solid #4caf50', boxShadow: 4 }}
                    src="/Assets/me.png"
                  />
                </Grid>
                
                {/* User Info - Right Side */}
                <Grid item xs={12} sm={8} md={9}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {user.firstName} {user.lastName} | BSCE
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      Senior Full Stack Software Engineer
                    </Typography>
                    <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                      {user.address?.city}, {user.address?.state} |  {user.phone} | {user.email}
                    </Typography>
                    <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {buttons.map((button) => (
                        <Button
                          key={button.name}
                          variant="outlined"
                          color="success"
                          size="small"
                          startIcon={button.icon}
                          onClick={() => openInNewTab(button.url)}
                          sx={{ borderRadius: 2 }}
                        >
                          {button.name}
                        </Button>
                      ))}
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<SaveAltIcon />}
                        sx={{ borderRadius: 2 }}
                        disabled={downloading}
                        onClick={downloadResumePdf}
                      >
                        {downloading ? 'Preparing PDF...' : 'Download Resume'}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 }, p:0 }}>
          <Summary summary={user.summary} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 }, p:0}}>
          <Education data={user.education} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: { xs: 3, md: 3 }, p:0}}>
          <Skills skills={user.skills} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: { xs: 4, md: 4 }, p:0 }}>
          <Experience experience={user.experience} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyResumePage;